import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';

import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { useClientPagedList } from '../../hooks/useClientPagedList';
import { useStaleSafeLoader } from '../../hooks/useStaleSafeLoader';
import {
  Loader2,
  RefreshCw,
  Edit,
  AlertTriangle,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  listInternalReferralSourcesAdminInternalReferralSourcesGet as listInternalReferralSources,
  createInternalReferralSourceAdminInternalReferralSourcesPost as createInternalReferralSource,
  updateInternalReferralSourceAdminInternalReferralSourcesSourceIdPut as updateInternalReferralSource,
  deleteInternalReferralSourceAdminInternalReferralSourcesSourceIdDelete as deleteInternalReferralSource,
} from '../../services/api/sdk.gen';
import type {
  AgencyClinicResponse,
  InternalReferralSourceResponse,
} from '../../services/api/types.gen';
import TablePaginationFooter from '../../components/TablePaginationFooter';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { ClinicSelect } from '../../components/clinic/ClinicSelect';
import ConfirmRetireDialog from './ConfirmRetireDialog';

const createFormSchema = z.object({
  first_name: z.string().trim().min(1, 'First name is required'),
  last_name: z.string().trim().min(1, 'Last name is required'),
});

// An existing row can carry no first name at all: the clinic info sync stores a
// roster label with no comma as all last name. Editing one must not demand a
// first name be invented before the last name can be corrected, so only the
// create dialog requires both halves.
const editFormSchema = createFormSchema.extend({
  first_name: z.string().trim(),
});

type SourceFormData = z.infer<typeof createFormSchema>;

const SOURCE_FORM_DEFAULTS: SourceFormData = { first_name: '', last_name: '' };

const SOURCE_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

function matchesReferralSource(
  source: InternalReferralSourceResponse,
  normalizedSearch: string
): boolean {
  return `${source.first_name} ${source.last_name}`
    .toLowerCase()
    .includes(normalizedSearch);
}

interface InternalReferralSourcesSectionProps {
  clinics: AgencyClinicResponse[];
  /** Incremented by the parent to force a reload (e.g. after a clinic info sync). */
  refreshToken?: number;
}

/**
 * Admin screen for the per-clinic internal referral source list: the agency's
 * own referral channels (marketers, liaisons, offices) that intake selects from
 * and the push records on the admission.
 */
const InternalReferralSourcesSection: React.FC<
  InternalReferralSourcesSectionProps
> = ({ clinics, refreshToken = 0 }) => {
  const { canEdit } = useAdminSectionAccess('referral-sources');
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [sources, setSources] = useState<InternalReferralSourceResponse[]>([]);
  const { isLoading, run: runLoad, invalidate } = useStaleSafeLoader();
  const [filterClinicId, setFilterClinicId] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const {
    search,
    setSearch,
    pageSize,
    setPageSize,
    pageIndex,
    setPageIndex,
    pageCount,
    filtered: filteredSources,
    visible: pagedSources,
    canPreviousPage,
    canNextPage,
  } = useClientPagedList({
    items: sources,
    matchesSearch: matchesReferralSource,
    pageSizeOptions: SOURCE_PAGE_SIZE_OPTIONS,
    // Back to the first page whenever the clinic changes.
    scopeKey: filterClinicId,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SourceFormData>({
    resolver: zodResolver(
      modalMode === 'create' ? createFormSchema : editFormSchema
    ),
    defaultValues: SOURCE_FORM_DEFAULTS,
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (clinics.length > 0) {
      setFilterClinicId(prev => prev ?? clinics[0].id);
    }
  }, [clinics]);

  const loadSources = useCallback(
    async (clinicId: string) => {
      // A load the admin has already moved on from must not write anything: its
      // rows would sit under a clinic name they don't belong to, and Edit or
      // Retire on one of them would act on the wrong clinic's source. The
      // spinner belongs to the newest load too, so a superseded one leaves it
      // alone rather than reporting itself finished.
      await runLoad(
        async () => {
          const result = await listInternalReferralSources({
            query: { clinic_id: clinicId },
            throwOnError: true,
          });
          return result.data ?? [];
        },
        {
          onSuccess: setSources,
          onError: e => {
            console.error('failed to load internal referral sources', e);
            showError('Failed to load internal referral sources');
          },
        }
      );
    },
    [runLoad, showError]
  );

  useEffect(() => {
    // Drop the outgoing clinic's rows before the new clinic's arrive, so what is
    // on screen always belongs to the clinic the selector names.
    setSources([]);
    if (filterClinicId) {
      void loadSources(filterClinicId);
    } else {
      // Retire any in-flight load so it cannot repopulate the emptied table,
      // and take over its spinner.
      invalidate();
    }
  }, [filterClinicId, loadSources, invalidate]);

  // A clinic info sync can create or rename sources under the clinic already on
  // screen. Reloading in its own effect keeps the rows in place while the new
  // ones arrive: there is no clinic change here, so nothing on screen is
  // mislabelled in the meantime.
  const isFirstRefreshRef = useRef(true);
  useEffect(() => {
    if (isFirstRefreshRef.current) {
      isFirstRefreshRef.current = false;
      return;
    }
    if (filterClinicId) {
      void loadSources(filterClinicId);
    }
    // Deliberately keyed on refreshToken alone: filterClinicId changes are
    // already handled by the effect above, and listing it here would reload
    // twice on a clinic switch.
  }, [refreshToken]);

  const handleCreate = () => {
    if (clinics.length === 0) return;
    setModalMode('create');
    setEditingId(null);
    setSubmitError(null);
    reset(SOURCE_FORM_DEFAULTS);
    setIsModalOpen(true);
  };

  const handleEdit = (source: InternalReferralSourceResponse) => {
    setModalMode('edit');
    setEditingId(source.id);
    setSubmitError(null);
    reset({ first_name: source.first_name, last_name: source.last_name });
    setIsModalOpen(true);
  };

  const handleSave = handleSubmit(
    async values => {
      if (!filterClinicId) return;
      setSubmitError(null);
      try {
        const firstName = values.first_name.trim();
        const lastName = values.last_name.trim();
        if (modalMode === 'create') {
          await createInternalReferralSource({
            body: {
              first_name: firstName,
              last_name: lastName,
              clinic_id: filterClinicId,
            },
            throwOnError: true,
          });
          showSuccess('Internal referral source created');
        } else {
          if (!editingId) return;
          await updateInternalReferralSource({
            path: { source_id: editingId },
            // A blank first name is omitted rather than sent: the update
            // schema rejects an empty one, and leaving the field out keeps
            // whatever the row already carries.
            body: firstName
              ? { first_name: firstName, last_name: lastName }
              : { last_name: lastName },
            throwOnError: true,
          });
          showSuccess('Internal referral source updated');
        }
        setIsModalOpen(false);
        await loadSources(filterClinicId);
      } catch (e) {
        console.error('failed to save internal referral source', e);
        // Surface a backend business message (e.g. duplicate name 409) when one
        // is provided; otherwise fall back to a generic message.
        const fallback =
          modalMode === 'create'
            ? 'Unable to create referral source. Please review the form and try again.'
            : 'Unable to update referral source. Please review the form and try again.';
        const message = getBusinessErrorMessage(e) ?? fallback;
        setSubmitError(message);
        showError(message);
      }
    },
    formErrors => {
      showError(
        formErrors.first_name?.message ??
          formErrors.last_name?.message ??
          'Please fix the errors in the form.'
      );
    }
  );

  const handleDelete = (sourceId: string) => {
    setPendingDeleteId(sourceId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    const sourceId = pendingDeleteId;
    setDeletingId(sourceId);
    try {
      await deleteInternalReferralSource({
        path: { source_id: sourceId },
        throwOnError: true,
      });
      showSuccess('Internal referral source retired');
      setIsDeleteDialogOpen(false);
      setPendingDeleteId(null);
      if (filterClinicId) {
        await loadSources(filterClinicId);
      }
    } catch (e) {
      console.error('failed to retire internal referral source', e);
      showError(
        getBusinessErrorMessage(e) ??
          'Failed to retire referral source. Please try again.'
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div className='space-y-6'>
        <div className='space-y-1.5'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
            <div>
              <p className='text-sm text-muted-foreground'>
                Referral channels intake can assign to an admission
              </p>
            </div>
            {canEdit && (
              <Button
                onClick={handleCreate}
                variant='outline'
                disabled={clinics.length === 0}
              >
                <Plus className='mr-2 h-4 w-4' />
                New Referral Source
              </Button>
            )}
          </div>
        </div>
        <div className='space-y-4'>
          {clinics.length === 0 ? (
            <div className='rounded-md border p-6 text-center text-muted-foreground text-sm'>
              Create a clinic first before adding referral sources.
            </div>
          ) : (
            <>
              <div className='flex flex-col lg:flex-row gap-4'>
                <div className='flex-1'>
                  <Input
                    placeholder='Search referral sources...'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className='w-full'
                  />
                </div>
                <div className='w-full lg:w-64'>
                  <ClinicSelect
                    clinics={clinics}
                    value={filterClinicId ?? ''}
                    onChange={e => setFilterClinicId(e.target.value)}
                    placeholder='Select a clinic'
                  />
                </div>
                <Button
                  onClick={() =>
                    filterClinicId && void loadSources(filterClinicId)
                  }
                  disabled={isLoading || !filterClinicId}
                  variant='outline'
                >
                  {isLoading ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <RefreshCw className='h-4 w-4' />
                  )}
                </Button>
              </div>

              <div className='rounded-md border overflow-auto'>
                <Table className='min-w-[420px]'>
                  <TableHeader>
                    <TableRow>
                      <TableHead>First Name</TableHead>
                      <TableHead>Last Name</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={3} className='text-center py-8'>
                          <Loader2 className='h-6 w-6 animate-spin mx-auto' />
                          <p className='text-sm text-muted-foreground mt-2'>
                            Loading referral sources...
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : filteredSources.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className='text-center py-8 text-muted-foreground'
                        >
                          {!filterClinicId
                            ? 'Select a clinic to view referral sources.'
                            : sources.length > 0
                              ? 'No referral sources match your search.'
                              : 'No referral sources found for this clinic. Click "New Referral Source" to create one.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagedSources.map(source => (
                        <TableRow key={source.id}>
                          <TableCell className='font-medium'>
                            {source.first_name}
                          </TableCell>
                          <TableCell className='font-medium'>
                            {source.last_name}
                          </TableCell>
                          <TableCell>
                            <div className='flex gap-2'>
                              {canEdit && (
                                <Button
                                  size='sm'
                                  variant='outline'
                                  aria-label={`Edit ${source.first_name} ${source.last_name}`}
                                  onClick={() => handleEdit(source)}
                                >
                                  <Edit className='h-4 w-4' />
                                </Button>
                              )}
                              {canEdit && (
                                <Button
                                  size='sm'
                                  variant='outline'
                                  aria-label={`Retire ${source.first_name} ${source.last_name}`}
                                  onClick={() => handleDelete(source.id)}
                                  disabled={deletingId === source.id}
                                  className='text-red-600 hover:text-red-700 hover:bg-red-50'
                                >
                                  {deletingId === source.id ? (
                                    <Loader2 className='h-4 w-4 animate-spin' />
                                  ) : (
                                    <Trash2 className='h-4 w-4' />
                                  )}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <TablePaginationFooter
                pageIndex={pageIndex}
                pageCount={pageCount}
                totalCount={filteredSources.length}
                pageSize={pageSize}
                pageSizeOptions={[...SOURCE_PAGE_SIZE_OPTIONS]}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                onPageSizeChange={setPageSize}
                onPreviousPage={() =>
                  setPageIndex(prev => Math.max(0, prev - 1))
                }
                onNextPage={() =>
                  setPageIndex(prev => Math.min(pageCount - 1, prev + 1))
                }
              />
            </>
          )}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[480px]'>
          <form onSubmit={handleSave}>
            <DialogHeader>
              <DialogTitle>
                {modalMode === 'create'
                  ? 'New Referral Source'
                  : 'Edit Referral Source'}
              </DialogTitle>
              <DialogDescription>
                {modalMode === 'create'
                  ? 'Add a referral source to this clinic.'
                  : 'Rename this referral source. Admissions already using it follow the new name.'}
              </DialogDescription>
            </DialogHeader>
            <div className='grid gap-4 py-4'>
              {submitError && (
                <Alert variant='destructive'>
                  <AlertDescription className='flex gap-2 items-center'>
                    <AlertTriangle className='h-4 w-4 shrink-0' />
                    {submitError}
                  </AlertDescription>
                </Alert>
              )}
              {/* items-start so a validation message under one field cannot
                  stretch the other cell and push its input out of line. */}
              <div className='grid grid-cols-1 items-start gap-4 sm:grid-cols-2'>
                <div className='grid gap-2'>
                  <label className='text-sm font-medium'>
                    First Name{modalMode === 'create' ? ' *' : ''}
                  </label>
                  <Input
                    inputSize='default'
                    {...register('first_name')}
                    aria-invalid={errors.first_name ? 'true' : 'false'}
                  />
                  {errors.first_name && (
                    <p className='text-xs text-red-600'>
                      {errors.first_name.message}
                    </p>
                  )}
                </div>
                <div className='grid gap-2'>
                  <label className='text-sm font-medium'>Last Name *</label>
                  <Input
                    inputSize='default'
                    {...register('last_name')}
                    aria-invalid={errors.last_name ? 'true' : 'false'}
                  />
                  {errors.last_name && (
                    <p className='text-xs text-red-600'>
                      {errors.last_name.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                )}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmRetireDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title='Retire referral source?'
        description='It stops appearing in the intake dropdown. Admissions already assigned to it keep their attribution and continue to appear in the Marketing Report.'
        onConfirm={() => void confirmDelete()}
        confirming={deletingId !== null}
        confirmVariant='default'
        showConfirmSpinner
        contentClassName='sm:max-w-[480px]'
      />
    </>
  );
};

export default InternalReferralSourcesSection;
